import { useState, useEffect, useRef } from 'react';

export interface WindowBreakpoints {
  isNarrow: boolean;      // < 480px width
  isMedium: boolean;      // 480px - 768px
  isWide: boolean;        // 768px - 1200px
  isUltraWide: boolean;   // > 1200px
  isTall: boolean;        // > 600px height
  isShort: boolean;       // < 400px height
  width: number;
  height: number;
  aspectRatio: number;
}

export interface ResponsiveConfig {
  denseModeThreshold: number;    // Width below which to use dense mode
  collapseSidebarThreshold: number;  // Width below which to collapse sidebar
  verticalLayoutThreshold: number;   // Width below which to use vertical layout
  compactToolbarThreshold: number;   // Width below which to compact toolbar
}

const defaultConfig: ResponsiveConfig = {
  denseModeThreshold: 600,
  collapseSidebarThreshold: 480,
  verticalLayoutThreshold: 400,
  compactToolbarThreshold: 520
};

export function useWindowResponsive(
  containerRef?: React.RefObject<HTMLElement>,
  config: Partial<ResponsiveConfig> = {}
): WindowBreakpoints & ResponsiveConfig & {
  isDenseMode: boolean;
  shouldCollapseSidebar: boolean;
  shouldUseVerticalLayout: boolean;
  shouldCompactToolbar: boolean;
} {
  const finalConfig = { ...defaultConfig, ...config };
  const [dimensions, setDimensions] = useState<WindowBreakpoints>({
    isNarrow: false,
    isMedium: false,
    isWide: true,
    isUltraWide: false,
    isTall: true,
    isShort: false,
    width: 1200,
    height: 800,
    aspectRatio: 1.5
  });

  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    const updateDimensions = (width: number, height: number) => {
      const aspectRatio = width / height;
      
      setDimensions({
        width,
        height,
        aspectRatio,
        isNarrow: width < 480,
        isMedium: width >= 480 && width < 768,
        isWide: width >= 768 && width < 1200,
        isUltraWide: width >= 1200,
        isTall: height > 600,
        isShort: height < 400
      });
    };

    if (containerRef?.current) {
      // Use ResizeObserver for precise container tracking
      if (!resizeObserverRef.current) {
        resizeObserverRef.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const { width, height } = entry.contentRect;
            updateDimensions(width, height);
          }
        });
      }
      
      resizeObserverRef.current.observe(containerRef.current);
      
      // Initial measurement
      const rect = containerRef.current.getBoundingClientRect();
      updateDimensions(rect.width, rect.height);
    } else {
      // Fallback to window dimensions
      const handleResize = () => {
        updateDimensions(window.innerWidth, window.innerHeight);
      };

      handleResize();
      window.addEventListener('resize', handleResize);
      
      return () => window.removeEventListener('resize', handleResize);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [containerRef]);

  return {
    ...dimensions,
    ...finalConfig,
    isDenseMode: dimensions.width < finalConfig.denseModeThreshold,
    shouldCollapseSidebar: dimensions.width < finalConfig.collapseSidebarThreshold,
    shouldUseVerticalLayout: dimensions.width < finalConfig.verticalLayoutThreshold,
    shouldCompactToolbar: dimensions.width < finalConfig.compactToolbarThreshold
  };
}